import React, { useMemo, useState } from "react";
import { ChevronRight, ChevronLeft, DollarSign, Scale, ShieldCheck, AlertTriangle, AlertOctagon, CheckCircle2, Info, Loader2, ListChecks, Wand2, FileCog, Accessibility } from "lucide-react";
import useCodeViolations from "../hooks/useCodeViolations";
import useIfcValidation from "../hooks/useIfcValidation";
import {
  computeSquareFootage,
  estimateCost,
  structuralFlags,
  formatUSD,
} from "../lib/inspectorMetrics";
import { auditAdaCompliance } from "../lib/adaMode";

/**
 * V2.0.29 — Right-rail Live Inspector.
 *
 * Real-time inspector panel that converts the editor from "draw and ask"
 * into "draw and watch the build viability update." Surfaces three
 * metric blocks:
 *
 *   1. Cost — $/sqft × current footprint (style-aware range).
 *   2. Structural — rooms with clear spans exceeding the joist limit.
 *   3. Compliance — green / yellow / red traffic light backed by the
 *      live `/api/designs/{id}/code-violations` endpoint.
 *
 * Compliance is debounced (1200ms after the last blueprint mutation) so
 * we don't hammer the endpoint while the user is mid-edit. The cost +
 * structural blocks recompute instantly from the in-memory blueprint.
 *
 * Hidden on mobile (< md) — the editor is already cramped there.
 */
const STORAGE_KEY = "blueprint.inspector.collapsed";

function severityColor(critical, warning) {
  if (critical > 0) return "red";
  if (warning > 0) return "yellow";
  return "green";
}

const COLOR_CLASSES = {
  red: {
    chip: "border-red-500/50 bg-red-950/30 text-red-300",
    label: "Action Required",
  },
  yellow: {
    chip: "border-amber-500/50 bg-amber-950/30 text-amber-300",
    label: "Review Needed",
  },
  green: {
    chip: "border-emerald-500/50 bg-emerald-950/30 text-emerald-300",
    label: "Compliant",
  },
  brass: {
    chip: "border-brass-500/40 bg-brass-950/30 text-brass-300",
  },
};

export default function LiveInspectorRail({ designId, blueprint, violationData: externalVD, loadingViolations: externalLoading, ifcValidation: externalIfcValidation, ifcValidationLoading: externalIfcLoading, onAutoResolveAll, autoResolving = false, accessibilityMode = "audit", onSetAccessibilityMode, onOpenAdaAudit, onResolveAllWithAda }) {
  // Backwards-compatible derived flag — many sub-blocks still key
  // styling off the boolean "is the user committed to ADA?".
  const adaMode = accessibilityMode === "on";
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  // V2.0.30 — Prefer externally-provided violation data (Editor.jsx
  // lifts the fetch so it can also feed FloorPlan2D's wall-violation
  // overlay). Fall back to the local hook for standalone usage.
  const local = useCodeViolations(designId, externalVD === undefined ? blueprint : null);
  const violationData = externalVD !== undefined ? externalVD : local.violationData;
  const loadingViolations = externalLoading !== undefined ? externalLoading : local.loading;

  // V2.0.29 — instant local computations
  const sq = useMemo(() => computeSquareFootage(blueprint), [blueprint]);
  const cost = useMemo(() => estimateCost(blueprint), [blueprint]);
  const struct = useMemo(() => structuralFlags(blueprint), [blueprint]);

  // V2.0.47 — ADA audit (door-width compliance preview). Lightweight pure
  // calc on every blueprint change — used by the Accessible Mode chip.
  const adaAudit = useMemo(() => auditAdaCompliance(blueprint), [blueprint]);
  const adaViolationsCount = adaAudit.violations.length;

  // V2.0.38 — Free pre-flight IFC validation for the BIM-ready chip.
  // Prefer externally-provided result (Editor.jsx lifts it so the
  // architect-handoff toast trigger can also observe clean ticks).
  const localIfc = useIfcValidation(
    designId,
    externalIfcValidation === undefined ? blueprint : null,
  );
  const ifcValidation = externalIfcValidation !== undefined ? externalIfcValidation : localIfc.result;
  const ifcValidationLoading = externalIfcLoading !== undefined ? externalIfcLoading : localIfc.loading;

  const setCollapsedAndPersist = (next) => {
    setCollapsed(next);
    try {
      localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
    } catch {
      /* ignore quota errors */
    }
  };

  const summary = violationData?.summary || { critical: 0, warning: 0, advisory: 0, total: 0 };
  const traffic = severityColor(summary.critical, summary.warning);

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsedAndPersist(false)}
        data-testid="inspector-expand-btn"
        title="Open Live Inspector"
        className="hidden md:flex flex-col items-center gap-3 px-2 py-3 border-l border-zinc-800 bg-zinc-950 hover:bg-zinc-900 transition-colors"
      >
        <ChevronLeft className="w-4 h-4 text-brass-400" />
        <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-400 [writing-mode:vertical-rl] rotate-180">
          Live Inspector
        </div>
        <span
          className={`w-2 h-2 rounded-full ${
            traffic === "red"
              ? "bg-red-500"
              : traffic === "yellow"
              ? "bg-amber-400"
              : "bg-emerald-500"
          }`}
          title={`Compliance: ${traffic}`}
        />
      </button>
    );
  }

  return (
    <aside
      data-testid="live-inspector-rail"
      className="hidden md:flex w-72 shrink-0 flex-col border-l border-zinc-800 bg-zinc-950 overflow-y-auto"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 sticky top-0 bg-zinc-950 z-10">
        <div className="flex items-center gap-2">
          <ListChecks className="w-3.5 h-3.5 text-brass-400" />
          <h3 className="font-mono text-[10px] uppercase tracking-widest text-brass-300">
            // Live Inspector
          </h3>
        </div>
        <button
          type="button"
          onClick={() => setCollapsedAndPersist(true)}
          data-testid="inspector-collapse-btn"
          title="Collapse"
          className="text-zinc-500 hover:text-zinc-100"
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* V2.0.49 — Unified Accessibility tab. Three-mode segmented
          control replaces the prior pill + chip combo:
            - OFF: quiet, no chip, no auto-bumps.
            - AUDIT: surfaces sub-spec doors via clickable chip + dialog.
            - ALWAYS ON: bumps every door to ADA target, persists
              `ada_mode:true`, locks wall drags at the ADA boundary. */}
      <section
        data-testid="inspector-ada-block"
        className="px-4 py-3 border-b border-zinc-800"
      >
        <div className="flex items-center gap-2 mb-2">
          <Accessibility className={
            accessibilityMode === "on"
              ? "w-3.5 h-3.5 text-violet-300"
              : "w-3.5 h-3.5 text-zinc-400"
          } />
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">
            Accessibility
          </span>
          {accessibilityMode === "on" && (
            <span
              data-testid="ada-toggle-state"
              className="ml-auto px-1.5 py-0.5 rounded-full bg-violet-300 text-violet-900 font-mono text-[9px] tracking-widest"
            >
              ON
            </span>
          )}
          {accessibilityMode === "audit" && adaViolationsCount > 0 && (
            <span
              data-testid="ada-audit-counter"
              className="ml-auto px-1.5 py-0.5 rounded-full bg-violet-500/20 text-violet-200 font-mono text-[9px] tracking-widest"
            >
              {adaViolationsCount}
            </span>
          )}
          {accessibilityMode === "off" && (
            <span
              data-testid="ada-toggle-state"
              className="ml-auto px-1.5 py-0.5 rounded-full bg-zinc-800 text-zinc-500 font-mono text-[9px] tracking-widest"
            >
              OFF
            </span>
          )}
        </div>

        {/* Three-segment selector */}
        <div
          role="radiogroup"
          aria-label="Accessibility mode"
          className="grid grid-cols-3 gap-1 p-0.5 rounded-full border border-zinc-800 bg-zinc-950"
        >
          {[
            { id: "off", label: "Off" },
            { id: "audit", label: "Audit" },
            { id: "on", label: "Always" },
          ].map((opt) => {
            const active = accessibilityMode === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                role="radio"
                aria-checked={active}
                data-testid={`ada-mode-${opt.id}`}
                onClick={() => onSetAccessibilityMode?.(opt.id)}
                className={
                  active
                    ? "px-2 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-widest bg-gradient-to-r from-indigo-600 to-violet-600 text-white transition-all"
                    : "px-2 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-violet-200 transition-colors"
                }
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Mode-specific footer */}
        {accessibilityMode === "on" && (
          <p
            data-testid="ada-mode-on-hint"
            className="mt-2 text-[10px] text-violet-200/80 leading-snug"
          >
            Doors auto-bumped to 32″ / 36″ (ICC A117.1). Wall drags blocked at the ADA boundary.
          </p>
        )}
        {accessibilityMode === "audit" && adaViolationsCount > 0 && (
          <button
            type="button"
            data-testid="ada-audit-chip"
            onClick={onOpenAdaAudit}
            className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded border border-violet-400/30 bg-violet-500/10 hover:bg-violet-500/20 hover:border-violet-400/60 font-mono text-[10px] uppercase tracking-widest text-violet-200 transition-colors"
          >
            <Accessibility className="w-3 h-3" />
            {adaViolationsCount} door{adaViolationsCount === 1 ? "" : "s"} below 32″ · audit
          </button>
        )}
        {accessibilityMode === "audit" && adaViolationsCount === 0 && (
          <button
            type="button"
            data-testid="ada-audit-chip-empty"
            onClick={onOpenAdaAudit}
            className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded border border-zinc-800 hover:border-violet-400/30 font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-violet-200 transition-colors"
          >
            <Accessibility className="w-3 h-3" />
            All doors meet ADA · review
          </button>
        )}
        {accessibilityMode === "off" && (
          <p className="mt-2 text-[10px] text-zinc-500 leading-snug">
            Accessibility checks paused. Switch to Audit to surface sub-spec doors.
          </p>
        )}
      </section>

      {/* COST BLOCK */}
      <section
        data-testid="inspector-cost-block"
        className="px-4 py-4 border-b border-zinc-800"
      >
        <div className="flex items-center gap-1.5 mb-2 text-zinc-400 font-mono text-[10px] uppercase tracking-widest">
          <DollarSign className="w-3 h-3" />
          Build Cost · Est.
        </div>
        <div className="font-display text-2xl text-zinc-50 leading-none mb-1">
          {formatUSD(cost.totalLow)}
          <span className="text-zinc-500 mx-1.5 text-base">–</span>
          {formatUSD(cost.totalHigh)}
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          {sq.totalSqFt.toFixed(0)} sqft · ${cost.perSqFtLow}–${cost.perSqFtHigh}/sqft
          {cost.state && (
            <span
              data-testid="cost-region-chip"
              className="ml-2 px-1.5 py-0.5 border border-brass-500/30 bg-brass-950/40 text-brass-300"
              title={cost.zipPrefix
                ? `Regional cost multiplier ×${cost.regionMultiplier.toFixed(2)} (ZIP ${cost.zipPrefix}xx metro override, RSMeans 2025)`
                : `Regional cost multiplier ×${cost.regionMultiplier.toFixed(2)} (RSMeans 2025 ${cost.state})`}
            >
              ×{cost.regionMultiplier.toFixed(2)} {cost.zipPrefix ? `${cost.zipPrefix}xx` : cost.state}
            </span>
          )}
        </div>
        <div className="mt-2 flex items-start gap-1.5 text-[10px] text-zinc-500 leading-snug">
          <Info className="w-3 h-3 shrink-0 mt-0.5" />
          Heuristic for {cost.style} new-build
          {cost.state ? `, regionalised for ${cost.state}` : ", no site set"}
          . Excludes land, finishes &amp; permits.
        </div>
      </section>

      {/* STRUCTURAL BLOCK */}
      <section
        data-testid="inspector-structural-block"
        className="px-4 py-4 border-b border-zinc-800"
      >
        <div className="flex items-center gap-1.5 mb-2 text-zinc-400 font-mono text-[10px] uppercase tracking-widest">
          <Scale className="w-3 h-3" />
          Structural Spans
        </div>
        {struct.length === 0 ? (
          <div
            data-testid="inspector-structural-clear"
            className={`inline-flex items-center gap-1.5 px-2 py-1 border ${COLOR_CLASSES.green.chip} font-mono text-[10px] uppercase tracking-widest`}
          >
            <CheckCircle2 className="w-3 h-3" />
            All spans within joist limit
          </div>
        ) : (
          <div className="space-y-2">
            <div
              className={`inline-flex items-center gap-1.5 px-2 py-1 border ${COLOR_CLASSES.yellow.chip} font-mono text-[10px] uppercase tracking-widest`}
            >
              <AlertTriangle className="w-3 h-3" />
              {struct.length} room{struct.length === 1 ? "" : "s"} need a beam
            </div>
            <ul className="text-xs text-zinc-300 space-y-1">
              {struct.slice(0, 5).map((s) => (
                <li
                  key={`${s.floor_index}-${s.id}`}
                  data-testid={`inspector-struct-row-${s.id}`}
                  className="flex items-start justify-between gap-2 border-l-2 border-amber-500/40 pl-2"
                >
                  <span className="truncate">{s.name}</span>
                  <span className="font-mono text-[10px] text-zinc-500 shrink-0">
                    {s.spanFt.toFixed(1)} ft span
                  </span>
                </li>
              ))}
              {struct.length > 5 && (
                <li className="text-zinc-500 italic text-[11px]">
                  …and {struct.length - 5} more
                </li>
              )}
            </ul>
            <p className="text-[10px] text-zinc-500 leading-snug">
              Rooms wider than {struct[0].maxFreeSpanFt.toFixed(0)} ft typically
              need a load-bearing beam mid-span. Discuss with your structural
              engineer.
            </p>
          </div>
        )}
      </section>

      {/* COMPLIANCE BLOCK */}
      <section
        data-testid="inspector-compliance-block"
        className="px-4 py-4 border-b border-zinc-800"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 text-zinc-400 font-mono text-[10px] uppercase tracking-widest">
            <ShieldCheck className="w-3 h-3" />
            Code Compliance
          </div>
          {loadingViolations && (
            <Loader2 className="w-3 h-3 text-brass-400 animate-spin" />
          )}
        </div>
        {!violationData && !loadingViolations && (
          <div className="text-[10px] text-zinc-500 italic">
            Will populate after first edit…
          </div>
        )}
        {violationData && (
          <>
            <div
              data-testid="inspector-compliance-traffic"
              className={`inline-flex items-center gap-1.5 px-2 py-1 border ${COLOR_CLASSES[traffic].chip} font-mono text-[10px] uppercase tracking-widest mb-3`}
            >
              {traffic === "red" && <AlertOctagon className="w-3 h-3" />}
              {traffic === "yellow" && <AlertTriangle className="w-3 h-3" />}
              {traffic === "green" && <CheckCircle2 className="w-3 h-3" />}
              {COLOR_CLASSES[traffic].label}
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <Tally label="Critical" value={summary.critical} color="red" />
              <Tally label="Warning" value={summary.warning} color="yellow" />
              <Tally label="Advisory" value={summary.advisory} color="brass" />
            </div>
            {(violationData.violations || []).length > 0 ? (
              <ul className="space-y-1.5 text-xs text-zinc-300">
                {violationData.violations.slice(0, 4).map((v, i) => (
                  <li
                    key={v.id || v.rule || `${v.severity || "adv"}-${i}-${(v.title || "").slice(0, 24)}`}
                    data-testid={`inspector-violation-row-${i}`}
                    className={`border-l-2 pl-2 ${
                      v.severity === "critical"
                        ? "border-red-500/60"
                        : v.severity === "warning"
                        ? "border-amber-500/60"
                        : "border-brass-500/40"
                    }`}
                  >
                    <div className="truncate">{v.title || v.rule || "Violation"}</div>
                    {v.detail && (
                      <div className="text-[10px] text-zinc-500 truncate">
                        {v.detail}
                      </div>
                    )}
                  </li>
                ))}
                {violationData.violations.length > 4 && (
                  <li className="text-zinc-500 italic text-[11px]">
                    …and {violationData.violations.length - 4} more
                  </li>
                )}
              </ul>
            ) : (
              <p className="text-[10px] text-zinc-500 leading-snug">
                No issues flagged by the residential code heuristics.
              </p>
            )}
            {/* V2.0.31 — Auto-Resolve All. One brass-pill click queues a
                single chat turn to the AI architect that addresses every
                flagged violation at once. Hidden when zero violations. */}
            {onAutoResolveAll && (violationData.violations || []).length > 0 && (
              <button
                type="button"
                data-testid="inspector-auto-resolve-all-btn"
                onClick={() => onAutoResolveAll(violationData.violations)}
                disabled={autoResolving}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 border border-brass-500/50 bg-brass-950/40 hover:bg-brass-900/40 text-brass-200 font-mono text-[11px] uppercase tracking-widest transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {autoResolving ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Wand2 className="w-3.5 h-3.5" />
                )}
                {autoResolving ? "Resolving…" : `Auto-Resolve All (${violationData.violations.length})`}
              </button>
            )}
            {/* V2.0.48 — Combo: Apply ADA bumps + then run the standard
                auto-resolver, in one click. Only surfaces when BOTH ADA
                violations AND code violations exist + ADA mode is OFF
                (so the user hasn't already global-bumped). */}
            {onResolveAllWithAda && (violationData.violations || []).length > 0 && adaViolationsCount > 0 && accessibilityMode === "audit" && (
              <button
                type="button"
                data-testid="inspector-resolve-with-ada-btn"
                onClick={onResolveAllWithAda}
                disabled={autoResolving}
                className="mt-2 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full border border-violet-400/40 bg-gradient-to-r from-indigo-600/30 to-violet-600/30 hover:from-indigo-600/50 hover:to-violet-600/50 text-violet-100 font-mono text-[11px] uppercase tracking-widest transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Accessibility className="w-3.5 h-3.5" />
                Apply ADA + Resolve Code
              </button>
            )}
          </>
        )}
      </section>

      {/* V2.0.38 — BIM-Ready chip. Free pre-flight IFC validation so
          users see green/amber/red BEFORE clicking checkout. Errors
          here are advisory (delivery still succeeds), but in practice
          a 0-error file opens cleanly in Revit / ArchiCAD. */}
      <section
        data-testid="inspector-ifc-block"
        className="px-4 py-4 border-b border-zinc-800"
      >
        <div className="flex items-center gap-1.5 mb-2 text-zinc-400 font-mono text-[10px] uppercase tracking-widest">
          <FileCog className="w-3 h-3" />
          BIM-Ready · IFC 4
        </div>
        {ifcValidationLoading && !ifcValidation ? (
          <div className="inline-flex items-center gap-1.5 px-2 py-1 border border-zinc-800 text-zinc-500 font-mono text-[10px] uppercase tracking-widest">
            <Loader2 className="w-3 h-3 animate-spin" /> Validating IFC…
          </div>
        ) : ifcValidation ? (
          ifcValidation.passed ? (
            <div
              data-testid="inspector-ifc-chip-clean"
              className={`inline-flex items-center gap-1.5 px-2 py-1 border ${COLOR_CLASSES.green.chip} font-mono text-[10px] uppercase tracking-widest`}
              title={`${(ifcValidation.byte_count || 0).toLocaleString()} bytes · validated against IFC 4 schema`}
            >
              <CheckCircle2 className="w-3 h-3" />
              IFC clean · 0 issues
            </div>
          ) : (
            <div
              data-testid="inspector-ifc-chip-advisory"
              className={`inline-flex items-center gap-1.5 px-2 py-1 border ${COLOR_CLASSES.amber.chip} font-mono text-[10px] uppercase tracking-widest`}
              title={(ifcValidation.errors || []).join("\n") || "Advisory only"}
            >
              <AlertTriangle className="w-3 h-3" />
              {ifcValidation.error_count} {ifcValidation.error_count === 1 ? "advisory" : "advisories"} — won't block delivery
            </div>
          )
        ) : (
          <div className="text-[10px] text-zinc-500 leading-snug">
            Validation pending — add at least one room to start checking IFC export readiness.
          </div>
        )}
        <div className="mt-2 text-[10px] text-zinc-500 leading-snug">
          Opens in Revit, ArchiCAD, Vectorworks, BricsCAD, BlenderBIM.
        </div>
      </section>

      <div className="px-4 py-3 text-[10px] text-zinc-600 leading-relaxed">
        Live preview. Final figures require sign-off from your licensed
        architect &amp; structural engineer.
      </div>
    </aside>
  );
}

function Tally({ label, value, color }) {
  const cls = COLOR_CLASSES[color] || COLOR_CLASSES.brass;
  return (
    <div
      className={`text-center border ${cls.chip} px-1 py-1.5`}
      data-testid={`inspector-tally-${label.toLowerCase()}`}
    >
      <div className="font-display text-base leading-none">{value}</div>
      <div className="font-mono text-[8px] uppercase tracking-widest mt-0.5 opacity-80">
        {label}
      </div>
    </div>
  );
}
